import { useState } from 'react';
import { languageName, scriptLanguage, type Message } from '@itsappy/shared';
import { Markdown } from '@/components/chat/Markdown';
import { LinkUnfurl } from '@/components/chat/LinkUnfurl';
import { useTranslateMessage, useTranslationStatus } from '@/hooks/translation';
import { useAuth } from '@/store/auth';
import { toast } from '@/store/ui';

/**
 * A message body, in the reader's language where there is one.
 *
 * The translation replaces the body rather than sitting under it. Showing both
 * was the first thing tried and it doubles the height of every message in a
 * bilingual channel — the transcript stops being scannable, which is most of
 * what a transcript is for. What is under it instead is one line saying the
 * message was translated and offering the original, because a reader who
 * suspects the machine got it wrong needs a way to check, and the author's
 * actual words are the only way to settle it.
 *
 * Nothing here renders at all when the reader has picked no language: an
 * English-only workspace sees exactly what it saw before.
 */
export function MessageBody({
  message,
  names,
  viewerId,
}: {
  message: Message;
  names: Map<string, string>;
  viewerId?: string;
}) {
  const language = useAuth((state) => state.user?.language ?? '');
  const status = useTranslationStatus().data;
  const translate = useTranslateMessage(message.channelId);
  const [showOriginal, setShowOriginal] = useState(false);

  const translation = language
    ? message.translations?.find((item) => item.lang === language)
    : undefined;
  /*
   * Whether to offer a Translate button at all.
   *
   * Only where the workspace has chosen not to translate automatically —
   * otherwise the server has already done it and the button would appear on
   * exactly the messages it decided were not worth translating.
   *
   * `sameLanguage` is what keeps it off the messages that need nothing. The
   * server's answer is the good one but only exists once something has
   * translated the message, so the script is asked too: it settles the
   * Arabic-and-English case this feature is mostly for, and abstains on Latin
   * scripts, where an offer that turns out to be redundant is the lesser
   * failure.
   */
  const isSelf = message.author.id === viewerId;
  const sameLanguage =
    message.sourceLang === language || scriptLanguage(message.body) === language;

  const canAsk =
    Boolean(language) &&
    !translation &&
    !sameLanguage &&
    !isSelf &&
    Boolean(status?.available && status.enabled && !status.automatic);

  const shown = translation && !showOriginal ? translation.body : message.body;

  return (
    <>
      <Markdown body={shown} names={names} viewerId={viewerId} />
      {/* Unfurls follow the original: the links in it are the same links, and
          an engine that rewrote one would unfurl something nobody posted. */}
      <LinkUnfurl body={message.body} />

      {translation && (
        <button
          type="button"
          onClick={() => setShowOriginal((current) => !current)}
          className="mt-0.5 text-xs text-slate-500 transition hover:text-slate-300"
        >
          {showOriginal
            ? 'Show translation'
            : `Translated${message.sourceLang ? ` from ${languageName(message.sourceLang)}` : ''} · Show original`}
        </button>
      )}

      {canAsk && (
        <button
          type="button"
          disabled={translate.isPending}
          onClick={() =>
            translate
              .mutateAsync({ messageId: message.id })
              .catch((error: Error) => toast.error('Could not translate', error.message))
          }
          className="mt-0.5 text-xs text-slate-500 transition hover:text-slate-300 disabled:opacity-60"
        >
          {translate.isPending ? 'Translating…' : `Translate to ${languageName(language)}`}
        </button>
      )}
    </>
  );
}
